import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Route, ArrowRight, X } from 'lucide-react';
import type { DatabaseState } from '@/types/database';

interface PathFinderProps {
  database: DatabaseState;
  onPathFound: (path: string[]) => void;
  onClose: () => void;
}

interface GraphNode {
  id: string;
  name: string;
  type: string;
}

// Dijkstra's algorithm implementation
function findShortestPath(
  startId: string,
  endId: string,
  nodes: GraphNode[],
  edges: Array<{ source: string; target: string }>
): string[] | null {
  // Build adjacency list
  const adjacency = new Map<string, Set<string>>();
  nodes.forEach(node => adjacency.set(node.id, new Set()));

  edges.forEach(edge => {
    adjacency.get(edge.source)?.add(edge.target);
    adjacency.get(edge.target)?.add(edge.source);
  });

  // Dijkstra's algorithm
  const distances = new Map<string, number>();
  const previous = new Map<string, string | null>();
  const unvisited = new Set(nodes.map(n => n.id));

  // Initialize
  nodes.forEach(node => {
    distances.set(node.id, Infinity);
    previous.set(node.id, null);
  });
  distances.set(startId, 0);

  while (unvisited.size > 0) {
    // Find unvisited node with smallest distance
    let current: string | null = null;
    let minDistance = Infinity;

    unvisited.forEach(nodeId => {
      const dist = distances.get(nodeId) || Infinity;
      if (dist < minDistance) {
        minDistance = dist;
        current = nodeId;
      }
    });

    if (!current || minDistance === Infinity) break;
    if (current === endId) break;

    unvisited.delete(current);

    // Update distances to neighbors
    const neighbors = adjacency.get(current) || new Set();
    neighbors.forEach(neighbor => {
      if (unvisited.has(neighbor)) {
        const alt = (distances.get(current!) || 0) + 1;
        if (alt < (distances.get(neighbor) || Infinity)) {
          distances.set(neighbor, alt);
          previous.set(neighbor, current);
        }
      }
    });
  }

  // Reconstruct path
  if (!previous.has(endId) || previous.get(endId) === null) {
    return null; // No path found
  }

  const path: string[] = [];
  let current: string | null = endId;

  while (current !== null) {
    path.unshift(current);
    current = previous.get(current) || null;
  }

  return path;
}

export function PathFinder({ database, onPathFound, onClose }: PathFinderProps) {
  const [startNodeId, setStartNodeId] = useState<string>('');
  const [endNodeId, setEndNodeId] = useState<string>('');
  const [path, setPath] = useState<string[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Create all nodes list
  const allNodes = useMemo<GraphNode[]>(() => {
    const nodes: GraphNode[] = [];

    database.studies?.forEach(study => {
      nodes.push({
        id: study.study_id,
        name: study.title.substring(0, 60) + '...',
        type: 'study'
      });
    });

    database.compounds?.forEach(compound => {
      nodes.push({
        id: compound.compound_id,
        name: compound.name,
        type: 'compound'
      });
    });

    database.mushrooms?.forEach(mushroom => {
      nodes.push({
        id: mushroom.mushroom_id,
        name: mushroom.common_name,
        type: 'mushroom'
      });
    });

    database.conditions?.forEach(condition => {
      nodes.push({
        id: condition.condition_id,
        name: condition.name,
        type: 'condition'
      });
    });

    return nodes.sort((a, b) => a.name.localeCompare(b.name));
  }, [database]);

  // Create edges
  const edges = useMemo(() => {
    const edgeList: Array<{ source: string; target: string }> = [];

    database.study_compounds?.forEach(sc => {
      edgeList.push({ source: sc.study_id, target: sc.compound_id });
    });

    database.interventions?.forEach(intervention => {
      edgeList.push({ source: intervention.study_id, target: intervention.mushroom_id });
    });

    database.study_conditions?.forEach(sc => {
      edgeList.push({ source: sc.study_id, target: sc.condition_id });
    });

    return edgeList;
  }, [database]);

  const handleFindPath = () => {
    if (!startNodeId || !endNodeId) return;

    setIsSearching(true);

    // Simulate delay for dramatic effect
    setTimeout(() => {
      const foundPath = findShortestPath(startNodeId, endNodeId, allNodes, edges);
      setPath(foundPath);

      if (foundPath) {
        onPathFound(foundPath);
      }

      setIsSearching(false);
    }, 500);
  };

  const getNodeById = (id: string) => allNodes.find(n => n.id === id);

  const getNodeTypeColor = (type: string) => {
    switch (type) {
      case 'study': return 'bg-[#A98A2C]';
      case 'compound': return 'bg-[#C26A1E]';
      case 'mushroom': return 'bg-[#B98B36]';
      case 'condition': return 'bg-[#B45F3D]';
      default: return 'bg-[#6E5743]';
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Route className="h-5 w-5 text-[#A98A2C]" />
            <CardTitle>Find Connection</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>
          Discover how any two nodes are connected in the research network
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Node Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Start Node</label>
            <Select value={startNodeId} onValueChange={setStartNodeId}>
              <SelectTrigger>
                <SelectValue placeholder="Select starting point..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {allNodes.map(node => (
                  <SelectItem key={node.id} value={node.id}>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {node.type}
                      </Badge>
                      <span className="text-sm truncate">{node.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">End Node</label>
            <Select value={endNodeId} onValueChange={setEndNodeId}>
              <SelectTrigger>
                <SelectValue placeholder="Select destination..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {allNodes.map(node => (
                  <SelectItem key={node.id} value={node.id}>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {node.type}
                      </Badge>
                      <span className="text-sm truncate">{node.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Find Path Button */}
        <Button
          onClick={handleFindPath}
          disabled={!startNodeId || !endNodeId || isSearching}
          className="w-full"
        >
          <Route className="mr-2 h-4 w-4" />
          {isSearching ? 'Searching...' : 'Find Shortest Path'}
        </Button>

        {/* Path Result */}
        {path && path.length > 0 && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Connection Found!</h4>
              <Badge variant="secondary">
                {path.length - 1} {path.length - 1 === 1 ? 'hop' : 'hops'}
              </Badge>
            </div>

            <div className="space-y-2">
              {path.map((nodeId, idx) => {
                const node = getNodeById(nodeId);
                if (!node) return null;

                const isLast = idx === path.length - 1;

                return (
                  <div key={nodeId}>
                    <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                      <div className={`h-3 w-3 rounded-full ${getNodeTypeColor(node.type)}`} />
                      <div className="flex-1">
                        <Badge variant="outline" className="text-xs mb-1">
                          {node.type}
                        </Badge>
                        <p className="text-sm font-medium">{node.name}</p>
                      </div>
                    </div>
                    {!isLast && (
                      <div className="flex justify-center py-1">
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="rounded-lg border bg-[#F4F2EC] dark:bg-[#0D0D0D] p-4">
              <p className="text-sm">
                <strong>Path Explanation:</strong> {path.map((nodeId, idx) => {
                  const node = getNodeById(nodeId);
                  return idx < path.length - 1 ? `${node?.name} → ` : node?.name;
                }).join('')}
              </p>
            </div>
          </div>
        )}

        {path && path.length === 0 && (
          <div className="mt-6 rounded-lg border border-[#C26A1E] bg-[#F4F2EC] dark:bg-[#0D0D0D] p-4">
            <p className="text-sm font-medium text-[#2A2A28] dark:text-[#FAFAF8]">
              No connection found between these nodes.
            </p>
            <p className="text-xs text-[#6E6B64] dark:text-[#C8A53D] mt-1">
              Try different nodes or they may exist in separate research clusters.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
